import { atom } from 'recoil';

export const defaultSettings = {
  roundingDecimals: 100,

  locationValues: {
    verylow: { txt: 'Rural', val: 0 },
    low: { txt: 'Industrial', val: 0.5 },
    medium: { txt: 'Urban', val: 1 },
    high: { txt: 'Offshore', val: 2 }
  },

  rigCapacityValues: {
    verylow: { txt: 'Superlight', val: 1 },
    low: { txt: 'Light', val: 2 },
    medium: { txt: 'Medium', val: 3 },
    high: { txt: 'Heavy', val: 4 },
    veryhigh: { txt: 'Superheavy', val: 5 }
  },

  circulationValues: {
    verylow: { txt: 'Low', val: 0.1 },
    low: { txt: 'Medium', val: 0.5 },
    medium: { txt: 'High', val: 1 }
  },

  pressRegimeValues: {
    verylow: { txt: 'Sub-hydrostatic', val: 0.5 },
    low: { txt: 'Hydrostatic', val: 0.1 },
    medium: { txt: 'Over pressured', val: 1 }
  },

  ucsValues: {
    verylow: { txt: 'Low', val: 0.1 },
    low: { txt: 'Medium', val: 0.33 },
    medium: { txt: 'High', val: 0.66 },
    high: { txt: 'Very high', val: 1 }
  },

  ddiValues: {
    verylow: { txt: 'Low', val: 0 },
    low: { txt: 'Medium', val: 0.33 },
    medium: { txt: 'High', val: 0.66 },
    high: { txt: 'Very high', val: 1 },
    calculated: { txt: 'Calculate', val: null }
  },

  phaseValues: {
    verylow: { txt: 'Liquid', val: 0.1 },
    low: { txt: 'Two-phase', val: 0.7 },
    medium: { txt: 'Vapour', val: 1 },
    high: { txt: 'Supercritical', val: 2 }
  },

  weights: {
    locationWeight: 0.5,
    residentialWeight: 0.3,
    sensitiveWeight: 0.2,
    rigCapacityWeight: 1,
    firstHoleDiameterWeight: 0.05,
    firstBOPDiameterWeight: 0.05,
    numberOfSectionsWeight: 0.25,
    numberOfSectionsScore: [0, 0.2, 0.4, 0.6, 0.8, 1, 1],
    verticalDepthWeight: 0.15,
    maxTemperatureWeight: 0.25,
    maxPressureWeight: 0.1,
    stimulationWeight: 0.05,
    scalingWeight: 0.05,
    corrosionWeight: 0.05,
    circulatingTempWeight: 0.6,
    pressureRegimeWeight: 0.2,
    ubompdWeight: 0.2,
    ucsWeight: 0.3,
    interbeddedWeight: 0.15,
    ddiWeight: 0.25,
    multiLateralWeight: 0.1,
    interceptionWeight: 0.2,
    phaseWeight: 0.5,
    hydrocarbonWeight: 0.25,
    toxicGasWeight: 0.25
  },

  categoryWeights: {
    location: 0.5,
    rigCapacity: 0.5,
    design: 0.3,
    construction: 0.2,
    drillingComplexity: 0.35,
    wellControl: 0.15
  },

  thresholds: {
    locationMultipliers: { verylow: 1, low: 2, medium: 3, high: 15 },
    firstHoleDiameter: { low: 27.5, high: 37.5 },
    firstBOP: { t1: 13.625, t2: 18.75, t3: 21.25 },
    maxDepth: 6200,
    temperature: { min: 150, max: 374 },
    pressure: [2000, 3000, 5000, 10000, 15000],
    barToPsi: 14.5038
  },

  wellboreGeometry: {
    1: [3.75, 6, 8.5, 12.25, 16, 24, 36],
    2: [4, 6.125, 9.5, 12.15, 16, 24, 36],
    3: [4.75, 7.875, 10.375, 14.5, 20, 30, 100],
    4: [5.875, 8.5, 12.25, 16, 24, 36, 100],
    5: [6, 8.5, 12.25, 16, 24, 36, 100],
    6: [6.5, 9.5, 14.5, 20, 30, 100, 100],
    7: [7.875, 10.625, 14.5, 20, 30, 100, 100],
    8: [8.5, 12.25, 16, 24, 36, 100, 100],
    9: [8.75, 12.25, 16, 24, 36, 100, 100],
    10: [9.5, 14.5, 20, 30, 100, 100, 100],
    11: [9.875, 14.5, 20, 30, 100, 100, 100],
    12: [10.625, 14.5, 20, 30, 100, 100, 100],
    13: [12.25, 16, 24, 36, 100, 100, 100],
    14: [14.75, 20, 30, 42, 100, 100, 100],
    15: [16, 24, 36, 100, 100, 100, 100],
    16: [17.5, 24, 36, 100, 100, 100, 100]
  }
};

const SETTINGS_STORAGE_KEY = 'complexityIndexSettings';

const loadStoredSettings = () => {
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return deepMerge(JSON.parse(JSON.stringify(defaultSettings)), parsed);
    }
  } catch (e) {
    console.warn('Failed to load settings from storage');
  }
  return defaultSettings;
};

const deepMerge = (target, source) => {
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (!target[key]) target[key] = {};
      deepMerge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
};

export const settingsState = atom({
  key: 'settingsState',
  default: loadStoredSettings(),
  effects: [
    ({ onSet }) => {
      onSet((newValue) => {
        try {
          localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(newValue));
        } catch (e) {
          console.warn('Failed to save settings to storage');
        }
      });
    }
  ]
});
